import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { GitFork, Check, X } from 'lucide-react';
import { useTheme } from '../ThemeContext';

export const ConditionNode = memo(({ data, selected }) => {
  const { isDark } = useTheme();

  return (
    <div className={`w-72 rounded-xl border transition-all ${
      isDark ? 'bg-slate-900 border-slate-800 shadow-xl' : 'bg-white border-slate-200 shadow-lg'
    } ${
      selected ? 'border-amber-500 ring-2 ring-amber-500/30' : (isDark ? 'hover:border-slate-700' : 'hover:border-slate-300')
    }`}>
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className={`!h-3.5 !w-3.5 !border-2 !bg-amber-500 hover:!scale-125 transition-transform ${
          isDark ? '!border-slate-900' : '!border-white'
        }`}
      />

      <div className={`flex items-center justify-between rounded-t-xl px-3.5 py-2.5 border-b ${
        isDark
          ? 'bg-gradient-to-r from-amber-950/80 to-amber-900/40 border-amber-500/20'
          : 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200'
      }`}>
        <div className="flex items-center gap-2">
          <div className={`flex h-6 w-6 items-center justify-center rounded-lg ${
            isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'
          }`}>
            <GitFork className="h-3.5 w-3.5" />
          </div>
          <span className={`text-xs font-bold uppercase tracking-wider ${
            isDark ? 'text-amber-200' : 'text-amber-900'
          }`}>
            {data.label || 'Condition (If/Else)'}
          </span>
        </div>
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
          isDark ? 'bg-amber-500/10 text-amber-300' : 'bg-amber-100 text-amber-700'
        }`}>
          BRANCH
        </span>
      </div>

      <div className="p-3.5 space-y-2.5 text-xs">
        <div className={`rounded-md p-2 font-mono text-[11px] border ${
          isDark
            ? 'bg-slate-800/80 border-slate-700/60 text-amber-200'
            : 'bg-slate-50 border-slate-200 text-amber-800'
        }`}>
          IF <span className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>{data.variable || 'variable'}</span> {data.operator || 'equals'} <span className="text-emerald-500 font-bold">"{data.value || ''}"</span>
        </div>

        <div className="space-y-1.5 pt-1">
          {/* True Branch */}
          <div className={`relative flex items-center justify-between rounded px-2.5 py-1 font-semibold text-[11px] border ${
            isDark
              ? 'bg-emerald-950/40 border-emerald-800/50 text-emerald-300'
              : 'bg-emerald-50 border-emerald-200 text-emerald-700'
          }`}>
            <div className="flex items-center gap-1.5">
              <Check className="h-3 w-3 text-emerald-500" />
              <span>YES / TRUE</span>
            </div>
            <Handle
              type="source"
              position={Position.Right}
              id="true"
              className={`!h-3 !w-3 !border-2 !bg-emerald-500 hover:!scale-125 transition-transform ${
                isDark ? '!border-slate-900' : '!border-white'
              }`}
            />
          </div>

          {/* False Branch */}
          <div className={`relative flex items-center justify-between rounded px-2.5 py-1 font-semibold text-[11px] border ${
            isDark
              ? 'bg-rose-950/40 border-rose-800/50 text-rose-300'
              : 'bg-rose-50 border-rose-200 text-rose-700'
          }`}>
            <div className="flex items-center gap-1.5">
              <X className="h-3 w-3 text-rose-500" />
              <span>NO / FALSE</span>
            </div>
            <Handle
              type="source"
              position={Position.Right}
              id="false"
              className={`!h-3 !w-3 !border-2 !bg-rose-500 hover:!scale-125 transition-transform ${
                isDark ? '!border-slate-900' : '!border-white'
              }`}
            />
          </div>
        </div>
      </div>
    </div>
  );
});
